// src/contexts/AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { getAuthToken, getUsername, getUserRole, getUserProfile, signOut, deriveCustomerNameFromEmail } from "../utils/auth";
import { fetchAdminUsers } from "../api/adminApi";
import { connectOrbieeChatSocket } from "../services/websocket";
import { showToast } from "../utils/toastManager";
import { resumeSession } from "../api/resumeSession";
import { fetchChatHistory } from "../api/fetchChatHistory";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

/**
 * The admin page's user list, or an empty one. Since 2026-10-18 the
 * `/auth_validate` reply no longer carries the list, so sign-in loads it the
 * way a page refresh always has. A failure here is not a failed sign-in: the
 * session is valid and only the admin page's data is missing, so the admin
 * is told once and the page shows an empty list (a refresh retries).
 */
const loadAdminUsersOrEmpty = async () => {
  try {
    const users = await fetchAdminUsers();
    return Array.isArray(users) ? users : [];
  } catch (error) {
    console.warn("[AuthContext] admin user list failed to load:", error);
    showToast({
      id: "admin-users-load",
      title: "Couldn't load the user list",
      message: "You're signed in. Refresh the admin page to try again.",
      severity: "error",
    });
    return [];
  }
};

// "Signed in elsewhere" (the session_takeover frame) is handled entirely by
// the avatar widget since 2026-10-28: the notice in the chat thread, the
// pinned toast and the one "Continue here" handler both buttons share live
// in AvatarLogoCode, next to the state they drive. This device stays signed
// in (until 2026-10-28 it signed itself out after a grace, and the toast
// lived here).
const bootstrapCustomerSession = async (
  customerId,
  { onResumeResolved, onChatHistoryResolved } = {}
) => {
  let resumeData = null;

  // 1. Resolve saved UI state first.
  try {
    resumeData = await resumeSession(customerId);
  } catch (error) {
    console.error("[AuthContext] Resume API failed:", error);
  }

  // Let the UI restore its page immediately, even if resume failed.
  // A null result means there is no reliable state to navigate from.
  onResumeResolved?.(resumeData);

  // 2. Fetch chat history and connect the socket concurrently.
  const chatHistoryPromise = fetchChatHistory(customerId)
    .then((history) => {
      onChatHistoryResolved?.(history);
      return history;
    })
    .catch((error) => {
      console.error("[AuthContext] Chat history failed:", error);
      onChatHistoryResolved?.(null);
      return null;
    });

  const socketPromise = connectOrbieeChatSocket(customerId).catch((error) => {
    console.warn("[AuthContext] WebSocket connection failed:", error);
    return null;
  });

  // Wait for both attempts before completing the overall bootstrap.
  const [chatHistory] = await Promise.all([
    chatHistoryPromise,
    socketPromise,
  ]);

  return { resumeData, chatHistory };
};

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    loading: true,
    bootstrapLoading: false,
    resumeLoading: false,
    resumeData: null,
    chatHistory: null,
  });

  // On mount: restore auth state from cookies (returning / page-refresh user).
  // Does NOT dispatch authStateChanged — this is not a new login event.
  useEffect(() => {
    const initializeAuth = async () => {
      const token    = getAuthToken();
      const username = getUsername();
      const role     = getUserRole();

      if (token && username) {
        if (role === "C") {
          const profile = getUserProfile();

          const user = profile
            ? { ...profile, role: profile.role || role }
            : {
                username,
                role,
                customer_name: deriveCustomerNameFromEmail(username),
              };

          // Keep the user authenticated while session bootstrap runs.
          setAuthState({
            user,
            loading: false,
            bootstrapLoading: true,
            resumeLoading: true,
            resumeData: null,
            chatHistory: null,
          });

          
          if (profile?.customer_id) { 
            await bootstrapCustomerSession(profile.customer_id, {
              onResumeResolved: (resumeData) => {
                setAuthState((current) => ({
                  ...current,
                  resumeData,
                  resumeLoading: false,
                }));
              },

              onChatHistoryResolved: (chatHistory) => {
                setAuthState((current) => ({
                  ...current,
                  chatHistory,
                  bootstrapLoading: false,
                }));
              },
            });

            // Fallback in case a future bootstrap change exits early.
            setAuthState((current) => ({
              ...current,
              bootstrapLoading: false,
              resumeLoading: false,
            }));
          }else {
            setAuthState((current) => ({
              ...current,
              bootstrapLoading: false,
              resumeLoading: false,
            }));
          }
        }else if (role === "A") {
          // Same loader as sign-in, same tolerance: the cookies are still
          // good, so a failed list is an empty admin page and a toast, not a
          // sign-out. (It used to sign out, which read as a broken login
          // whenever the users function was down — the user's call, 2026-10-19.)
          const profile = getUserProfile();
          const users = await loadAdminUsersOrEmpty();
          setAuthState({
            user: { ...(profile || {}), username: profile?.username || username, role, users },
            loading: false,
          });
        }
      } else {
        setAuthState({ user: null, loading: false });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Called by SignInModal after a successful Firebase login + validateUser.
   * Accepts the profile `validateUser` built — or, from AdminPage, the
   * current user with an edited user list.
   *
   * Sequence (in order):
   *  1. Update React auth state — all useAuth() consumers re-render immediately.
   *  2. Await WebSocket open (or timeout) — avatar trigger is deferred until WS is stable.
   *  3. Dispatch "authStateChanged" — ChatBot picks this up and plays the greeting.
   */
  const login = async (profile) => {
    // 0. Admins: fetch the user list unless the caller supplied one —
    //    AdminPage re-calls login() with the list it just edited, and a
    //    refetch must not overwrite that.
    const user =
      profile?.role === "A" && !Array.isArray(profile.users)
        ? { ...profile, users: await loadAdminUsersOrEmpty() }
        : profile;

    // 2. Publish the authenticated user immediately.
    
  
    setAuthState({
      user,
      loading: false,
      bootstrapLoading: user?.role === "C" && Boolean(user?.customer_id),
      resumeLoading: user?.role === "C" && Boolean(user?.customer_id),
      resumeData: null,
      chatHistory: null,
    });


    // 3. Bootstrap customer session in the required order.
  
    if (user?.role === "C" && user?.customer_id) {
      await bootstrapCustomerSession(user.customer_id, {
        onResumeResolved: (resumeData) => {
          setAuthState((current) => ({
            ...current,
            resumeData,
            resumeLoading: false,
          }));
        },

        onChatHistoryResolved: (chatHistory) => {
          setAuthState((current) => ({
            ...current,
            chatHistory,
            bootstrapLoading: false,
          }));
        },
      });

      setAuthState((current) => ({
        ...current,
        bootstrapLoading: false,
        resumeLoading: false,
      }));
    }

    window.dispatchEvent(
      new CustomEvent("authStateChanged", { detail: user })
    );
  };

  const logout = () => {
    signOut();
    setAuthState({ user: null, loading: false });
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

